/**
 * Client-side CSV parsing for Market Intelligence's manual data entry. No
 * server round-trip: a file picked in the browser is parsed here and the
 * rows are handed straight to the mock client. Deliberately simple — one row
 * per line, comma-separated, no quoted-field support — because the
 * acceptance criteria asks for manual entry and CSV import, not a general
 * CSV engine.
 */

import { isIsoDate } from '@/lib/filters'

import { COMPETITOR_FREQUENCIES, type CompetitorFrequency } from '@/api/market'

export interface ParsedCompetitorRow {
  name: string
  domain: string
  brand: string
  frequency: CompetitorFrequency
  typicalTicketPrice: number
  prizeCategories: string[]
}

export interface ParsedTrackedCompetitionRow {
  competitorId: string
  prize: string
  category: string
  ticketPrice: number
  maxEntries: number
  instantWins: number
  startDate: string
  endDate: string
  promotionalMessaging: string
}

export interface CsvParseResult<T> {
  rows: T[]
  errors: string[]
}

function splitLines(csv: string): string[] {
  return csv
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}

function splitCells(line: string): string[] {
  return line.split(',').map((cell) => cell.trim())
}

function normaliseFrequency(value: string): CompetitorFrequency | null {
  const key = value.trim().toLowerCase().replace(/\s+/g, '_')
  return (COMPETITOR_FREQUENCIES as readonly string[]).includes(key) ? (key as CompetitorFrequency) : null
}

export const COMPETITOR_CSV_HEADER = 'name,domain,brand,frequency,typicalTicketPrice,prizeCategories'
export const TRACKED_COMPETITION_CSV_HEADER =
  'competitorName,prize,category,ticketPrice,maxEntries,instantWins,startDate,endDate,promotionalMessaging'

export function parseCompetitorCsv(csv: string): CsvParseResult<ParsedCompetitorRow> {
  const lines = splitLines(csv)
  const rows: ParsedCompetitorRow[] = []
  const errors: string[] = []
  if (lines.length === 0) return { rows, errors: ['The file is empty.'] }

  const firstCells = splitCells(lines[0] as string)
  const start = (firstCells[0] ?? '').toLowerCase() === 'name' ? 1 : 0
  if (start === 0) errors.push(`No header row found; expecting "${COMPETITOR_CSV_HEADER}". Parsing every line as data.`)

  for (let index = start; index < lines.length; index += 1) {
    const rowNumber = index + 1
    const [name, domain, brand, frequencyRaw, priceRaw, categoriesRaw] = splitCells(lines[index] as string)
    if (!name || !domain || !brand) {
      errors.push(`Row ${rowNumber}: name, domain and brand are required.`)
      continue
    }
    const frequency = normaliseFrequency(frequencyRaw ?? '')
    if (!frequency) {
      errors.push(`Row ${rowNumber}: "${frequencyRaw ?? ''}" is not a recognised frequency (weekly, fortnightly, monthly, ad_hoc).`)
      continue
    }
    const typicalTicketPrice = Number(priceRaw)
    if (!Number.isFinite(typicalTicketPrice) || typicalTicketPrice < 0) {
      errors.push(`Row ${rowNumber}: "${priceRaw ?? ''}" is not a valid ticket price.`)
      continue
    }
    const prizeCategories = (categoriesRaw ?? '')
      .split(';')
      .map((value) => value.trim())
      .filter(Boolean)
    rows.push({ name, domain, brand, frequency, typicalTicketPrice, prizeCategories })
  }
  return { rows, errors }
}

export function parseTrackedCompetitionCsv(
  csv: string,
  resolveCompetitorId: (name: string) => string | undefined,
): CsvParseResult<ParsedTrackedCompetitionRow> {
  const lines = splitLines(csv)
  const rows: ParsedTrackedCompetitionRow[] = []
  const errors: string[] = []
  if (lines.length === 0) return { rows, errors: ['The file is empty.'] }

  const firstCells = splitCells(lines[0] as string)
  const start = (firstCells[0] ?? '').toLowerCase() === 'competitorname' ? 1 : 0
  if (start === 0) {
    errors.push(`No header row found; expecting "${TRACKED_COMPETITION_CSV_HEADER}". Parsing every line as data.`)
  }

  for (let index = start; index < lines.length; index += 1) {
    const rowNumber = index + 1
    const [competitorName, prize, category, ticketPriceRaw, maxEntriesRaw, instantWinsRaw, startDate, endDate, ...rest] =
      splitCells(lines[index] as string)
    if (!competitorName || !prize || !category) {
      errors.push(`Row ${rowNumber}: competitorName, prize and category are required.`)
      continue
    }
    const competitorId = resolveCompetitorId(competitorName)
    if (!competitorId) {
      errors.push(`Row ${rowNumber}: no tracked competitor named "${competitorName}". Add the competitor first.`)
      continue
    }
    const ticketPrice = Number(ticketPriceRaw)
    const maxEntries = Number(maxEntriesRaw)
    if (!Number.isFinite(ticketPrice) || ticketPrice < 0 || !Number.isFinite(maxEntries) || maxEntries <= 0) {
      errors.push(`Row ${rowNumber}: ticketPrice and maxEntries must be positive numbers.`)
      continue
    }
    if (!isIsoDate(startDate ?? null) || !isIsoDate(endDate ?? null)) {
      errors.push(`Row ${rowNumber}: startDate and endDate must be ISO dates (YYYY-MM-DD).`)
      continue
    }
    if ((endDate as string) < (startDate as string)) {
      errors.push(`Row ${rowNumber}: endDate must be on or after startDate.`)
      continue
    }
    const instantWins = Number(instantWinsRaw)
    rows.push({
      competitorId,
      prize,
      category,
      ticketPrice,
      maxEntries,
      instantWins: Number.isFinite(instantWins) && instantWins > 0 ? Math.round(instantWins) : 0,
      startDate: startDate as string,
      endDate: endDate as string,
      promotionalMessaging: rest.join(', '),
    })
  }
  return { rows, errors }
}
